import axios from 'axios';

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't redirect to login for the public store
        // Just log the error and let components handle it
        if (error.response?.status === 401) {
          console.warn('Unauthorized request - authentication may be required for this endpoint');
        }
        return Promise.reject(error);
      }
    );
  }

  // Card endpoints
  async getCards(params = {}) {
    const response = await this.client.get('/api/cards', { params });
    return response.data;
  }

  async getCard(id) {
    const response = await this.client.get(`/api/cards/${id}`);
    return response.data;
  }

  async getCardImages(cardId) {
    const response = await this.client.get(`/api/cards/${cardId}/images`);
    return response.data;
  }

  // Category taxonomy (active, ordered categories + version) — store home builds rails from this.
  async getTaxonomy() {
    const response = await this.client.get('/api/taxonomy');
    return response.data;
  }

  // Store/Cart endpoints
  async getCart() {
    const response = await this.client.get('/api/cart');
    return response.data;
  }

  async addToCart(cardId, quantity = 1) {
    const response = await this.client.post('/api/cart', {
      cardId,
      quantity
    });
    return response.data;
  }

  async updateCartItem(itemId, quantity) {
    const response = await this.client.put(`/api/cart/${itemId}`, {
      quantity
    });
    return response.data;
  }

  async removeFromCart(itemId) {
    const response = await this.client.delete(`/api/cart/${itemId}`);
    return response.data;
  }

  async clearCart() {
    const response = await this.client.delete('/api/cart');
    return response.data;
  }

  // Order endpoints
  async createOrder(orderData) {
    const response = await this.client.post('/api/store/orders', orderData);
    return response.data;
  }

  async getOrders(params = {}) {
    const response = await this.client.get('/api/orders', { params });
    return response.data;
  }

  async getOrder(id) {
    const response = await this.client.get(`/api/orders/${id}`);
    return response.data;
  }

  async confirmOrder(orderId) {
    const response = await this.client.post(`/api/orders/${orderId}/confirm`);
    return response.data;
  }

  async previewCheckoutTax(cart, billing) {
    const response = await this.client.post('/api/store/checkout-tax', { cart, billing });
    return response.data;
  }

  async createPaymentIntent(cart, billing) {
    const response = await this.client.post('/api/store/payment-intent', { cart, billing });
    return response.data;
  }

  // Upload text image to S3 (for cart items)
  async uploadTextImage(imageData) {
    const response = await this.client.post('/api/store/upload-text-image', {
      imageData,
      mimeType: 'image/png',
    });
    return response.data;
  }

  // User favorites
  async getFavorites() {
    const response = await this.client.get('/api/favorites');
    return response.data;
  }

  async addToFavorites(cardId) {
    const response = await this.client.post('/api/favorites', { cardId });
    return response.data;
  }

  async removeFromFavorites(cardId) {
    const response = await this.client.delete(`/api/favorites/${cardId}`);
    return response.data;
  }

  // Personalization endpoints
  async personalizeCard(cardId, personalizationData) {
    const response = await this.client.post(`/api/cards/${cardId}/personalize`, personalizationData);
    return response.data;
  }

  async getPersonalizationOptions(cardId) {
    const response = await this.client.get(`/api/cards/${cardId}/personalization-options`);
    return response.data;
  }

  // Obituary endpoints
  async getObituary(id) {
    const response = await this.client.get(`/api/obituaries/${id}`);
    return response.data;
  }

  getObituaryPhotoUrl(obituaryId) {
    const baseURL = this.client.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseURL}/api/obituaries/${obituaryId}/photo`;
  }

  // Analytics endpoints
  async incrementStoreVisits(obituaryId, visitorIdentifier) {
    const response = await this.client.post(`/api/obituaries/${obituaryId}/store-visit-unique`, {
      visitorIdentifier
    });
    return response.data;
  }

  // Message suggestions endpoint (Bedrock)
  async getMessageSuggestions(obituaryId, options = {}) {
    const { recipientName, relationship, tone, length, additionalContext } = options;
    const response = await this.client.post(
      '/api/message-suggestions',
      {
        obituaryId,
        recipientName,
        relationship,
        tone,
        length,
        additionalContext,
      }
    );
    return response.data;
  }
}

const apiClient = new ApiClient();
export default apiClient;
